import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import formattedMessage from './languageModule'
import MerchantModal from '@nexidigital/merchantmodal'
import Toggle from 'Hoc/ToggleComponent'
import StoreSelect from 'Components/StoreSelect'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import DatePicker from '@nexidigital/datepicker'
import moment from 'moment'
import { filterKeys } from '../../routes/PayByLink/modules/utils'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Eraser } from '@nexidigital/iconlibrary'
import './PayByLinkFilterModal.scss'
import { useIntl } from 'react-intl'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import { getDatePickerLocale } from 'Utility/globalMethods'

const statusesOptions = [
  {
    id: 'NOT_PAID',
    label: formattedMessage.linkStatusOptionNotPaid,
    checked: false
  },
  {
    id: 'PAID',
    label: formattedMessage.linkStatusOptionPaid,
    checked: false
  },
  {
    id: 'FAILED',
    label: formattedMessage.linkStatusOptionFailed,
    checked: false
  },
  {
    id: 'EXPIRED',
    label: formattedMessage.linkStatusOptionExpired,
    checked: false
  }
]

const initialFilters = {
  [filterKeys.SELECTED_STORES]: [],
  [filterKeys.SELECTED_STATUSES]: statusesOptions,
  [filterKeys.DATE_FROM]: '',
  [filterKeys.DATE_TO]: ''
}

const parseDDMMYYYY = (value) => {
  const [day, month, year] = value.split('/')
  return new Date(`${year}-${month}-${day}`)
}

const isValidDateFormat = (value) => /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(value)

const validationSchema = Yup.object().shape({
  [filterKeys.DATE_FROM]: Yup.string()
    .nullable()
    .test('valid-format', formattedMessage.dateFormatError, (value) => {
      if (!value) return true
      return isValidDateFormat(value)
    })
    .test('not-too-old', formattedMessage.outOfRangeError, (value) => {
      if (!value || !isValidDateFormat(value)) return true
      const date = parseDDMMYYYY(value)
      const limit = new Date()
      limit.setMonth(limit.getMonth() - 12)
      return date >= limit
    })
    .test('not-in-future', formattedMessage.startDateAfterTodayError, (value) => {
      if (!value || !isValidDateFormat(value)) return true
      const date = parseDDMMYYYY(value)
      const now = new Date()
      return date <= now
    })
    .test('before-end-date', formattedMessage.startDateAfterEndDateError, function (value) {
      const { [filterKeys.DATE_TO]: endDateStr } = this.parent
      if (!value || !isValidDateFormat(value)) return true
      if (!endDateStr || !isValidDateFormat(endDateStr)) return true

      const startDate = parseDDMMYYYY(value)
      const endDate = parseDDMMYYYY(endDateStr)

      return startDate <= endDate
    }),

  [filterKeys.DATE_TO]: Yup.string()
    .nullable()
    .test('valid-format', formattedMessage.dateFormatError, (value) => {
      if (!value) return true
      return isValidDateFormat(value)
    })
    .test('not-too-old', formattedMessage.outOfRangeError, (value) => {
      if (!value || !isValidDateFormat(value)) return true
      const date = parseDDMMYYYY(value)
      const limit = new Date()
      limit.setMonth(limit.getMonth() - 12)
      return date >= limit
    })
    .test('not-in-future', formattedMessage.endDateAfterTodayError, (value) => {
      if (!value || !isValidDateFormat(value)) return true
      const date = parseDDMMYYYY(value)
      const now = new Date()
      return date <= now
    })
})

const PayByLinkFilterModal = (props) => {
  const [filters, setFilters] = useState(initialFilters)
  const { isMobile } = useMobileDetect()
  const intl = useIntl()

  const formik = useFormik({
    initialValues: {
      [filterKeys.DATE_FROM]: '',
      [filterKeys.DATE_TO]: ''
    },
    validationSchema,
    onSubmit: (values) => handleSubmit(values)
  })

  useEffect(() => {
    const presetFilters = {
      [filterKeys.SELECTED_STORES]: props.filters[filterKeys.SELECTED_STORES] || [],
      [filterKeys.SELECTED_STATUSES]: statusesOptions.map(option => ({
        ...option,
        checked: props.filters[filterKeys.SELECTED_STATUSES]?.includes(option.id)
      })),
      [filterKeys.DATE_FROM]: props.filters[filterKeys.DATE_FROM] || '',
      [filterKeys.DATE_TO]: props.filters[filterKeys.DATE_TO] || ''
    }

    setFilters(presetFilters)

    formik.setValues({
      [filterKeys.DATE_FROM]: props.filters[filterKeys.DATE_FROM] || '',
      [filterKeys.DATE_TO]: props.filters[filterKeys.DATE_TO] || ''
    })
  }, [props.filters])

  const setSelectedStores = (selectedStores) => {
    setFilters(currentFilters => ({
      ...currentFilters,
      [filterKeys.SELECTED_STORES]: selectedStores
    }))
  }

  const handleCheckboxChange = (id, checked) => {
    const updatedOptions = filters[filterKeys.SELECTED_STATUSES].map(option => {
      if (option.id === id) {
        return { ...option, checked }
      }
      return option
    })
    setFilters(currentFilters => ({
      ...currentFilters,
      [filterKeys.SELECTED_STATUSES]: updatedOptions
    }))
  }

  const handleDateChange = (type, date, shouldValidate = true) => {
    setFilters(currentFilters => ({
      ...currentFilters,
      [type]: date
    }))
    formik.setFieldValue(type, date, shouldValidate)
    formik.setFieldTouched(type, true, false)
  }

  const handleResetFilters = () => {
    setFilters(initialFilters)
    formik.resetForm()
  }

  const formatFilters = (filters) => ({
    filters: {
      ...filters,
      [filterKeys.DATE_FROM]: formik.values[filterKeys.DATE_FROM],
      [filterKeys.DATE_TO]: formik.values[filterKeys.DATE_TO],
      [filterKeys.SELECTED_STATUSES]: filters[filterKeys.SELECTED_STATUSES].filter(option => option.checked).map(option => option.id)
    },
    page: props.page,
    size: props.size
  })

  const handleSubmit = () => {
    const formattedFilters = formatFilters(filters)
    props.actions.doGetGeneratedLinks({
      ...formattedFilters,
      intl
    })
    props.actions.doHidePopup()
  }

  const emptyFilters = () => {
    const { [filterKeys.SELECTED_STORES]: selectedStores, [filterKeys.SELECTED_STATUSES]: selectedStatuses } = filters
    const dateFrom = formik.values[filterKeys.DATE_FROM]
    const dateTo = formik.values[filterKeys.DATE_TO]
    return selectedStores.length === 0 && selectedStatuses.every(option => !option.checked) && !dateFrom && !dateTo
  }

  return (
    <Toggle>
      <MerchantModal
        iconName='errorCheck'
        onClose={() => props.actions.doHidePopup()}
        topBarTitle={formattedMessage.title}
        primaryAction={formik.handleSubmit} // eslint-disable-line
        primaryButtonLabel={formattedMessage.applyFilters}
        primaryButtonDisabled={!formik.isValid || formik.isSubmitting || emptyFilters()}
        idResetFilterCta='resetFilter'
        resetFilterLabel={formattedMessage.resetFilterLabel}
        onResetFilter={() => { handleResetFilters() }}
        disabledResetFilter={!formik.isValid || formik.isSubmitting || emptyFilters()}
        iconReset={<Eraser size='24' type='line' />}
      >
        <div className='filterPopupContent'>
          <MerchantGenericCard empty>
            {props.storeOptions.length > 1 && (
              <div>
                <h2 className='filterPopupTitle'>
                  {formattedMessage.titleSectionFacilities}
                </h2>
                <StoreSelect
                  storeOptions={props.storeOptions}
                  selectedStores={filters[filterKeys.SELECTED_STORES]}
                  onChangeSelectedStores={setSelectedStores}
                  isMultiSelection
                />
              </div>
            )}
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionLinkStatus}
              </h2>
              <div className='filterPopupCheckboxes'>
                <div>
                  <PortalCheckBox
                    onChange={({ id, checked }) => handleCheckboxChange(id, checked)}
                    options={filters[filterKeys.SELECTED_STATUSES]}
                    radioGroupName='filter-selector'
                    centered
                    grid
                  />
                </div>
              </div>
            </div>
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionCreationPeriod}
              </h2>
              <p className='filterPopupSubtitle'>
                {formattedMessage.subtitleSectionCreationPeriod}
              </p>
              <div>
                <DatePicker
                  id='filtersModalDatePicker'
                  isRange
                  smartYearNavigation
                  id1='filtersModalDatePicker1'
                  label1={formattedMessage.datePickerLabelFrom}
                  name1={filterKeys.DATE_FROM}
                  placeholder1={intl.formatMessage({ id: 't.app.datePicker.placeholder.default', defaultMessage: 'gg/mm/aaaa' })}
                  id2='filtersModalDatePicker2'
                  label2={formattedMessage.datePickerLabelTo}
                  name2={filterKeys.DATE_TO}
                  value1={formik.values[filterKeys.DATE_FROM]}
                  value2={formik.values[filterKeys.DATE_TO]}
                  onChange1={(date) => handleDateChange(filterKeys.DATE_FROM, date)}
                  onChange2={(date) => handleDateChange(filterKeys.DATE_TO, date)}
                  minDate={moment().subtract(12, 'months').add(1, 'day')}
                  maxDate={moment()}
                  error1={formik.touched[filterKeys.DATE_FROM] && formik.errors[filterKeys.DATE_FROM]}
                  errorMessage1={formik.touched[filterKeys.DATE_FROM] && formik.errors[filterKeys.DATE_FROM]}
                  error2={formik.touched[filterKeys.DATE_TO] && formik.errors[filterKeys.DATE_TO]}
                  errorMessage2={formik.touched[filterKeys.DATE_TO] && formik.errors[filterKeys.DATE_TO]}
                  placeholder2={intl.formatMessage({ id: 't.app.datePicker.placeholder.default', defaultMessage: 'gg/mm/aaaa' })}
                  onBlur1={(value) => handleDateChange(filterKeys.DATE_FROM, value)}
                  onBlur2={(value) => handleDateChange(filterKeys.DATE_TO, value)}
                  isMobile={isMobile}
                  labelYear={intl.formatMessage({ id: 't.app.year' })}
                  labelMonth={intl.formatMessage({ id: 't.app.month' })}
                  locale={getDatePickerLocale(intl.locale)}
                  labelsLocale={intl.locale}
                />
              </div>
            </div>
          </MerchantGenericCard>
        </div>
      </MerchantModal>
    </Toggle>
  )
}

PayByLinkFilterModal.propTypes = {
  actions: PropTypes.object.isRequired,
  filters: PropTypes.shape({
    selectedStores: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired
  }),
  page: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  storeOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }))
}

PayByLinkFilterModal.defaultProps = {
  actions: {},
  filters: {
    selectedStores: [],
    selectedStatuses: [],
    dateFrom: null,
    dateTo: null
  },
  page: 0,
  size: 15,
  storeOptions: []
}

export default PayByLinkFilterModal
